import type { ConditionCategory, ConditionType } from "@sinch/types/sinch.types";
import { sortLocalized } from "@sinch/utils/array";
import { flatten, isNil } from "ramda";
import { isArray } from "ramda-adjunct";
import type { NestedOptions } from "@sinch/components/form/MultilevelSelect/types";
import type { RateConditionSubjectFragment } from "@sinch/components/application/RatelistBuilder/RateBuilderRowContent/ConditionInput/queries.types";

interface ConditionSubjectRef {
  category: ConditionCategory | null;
  subjectId: string | null;
  type: ConditionType | null;
}

/**
 * Label path for a condition whose subject (badge, attribute) is gone: the category label followed by the given
 * "deleted" label. Empty while the category subjects are still loading (the subject may simply not be there yet),
 * for conditions without a subject id and for an unknown category.
 */
export const getDeletedSubjectLabelPath = (
  options: NestedOptions<ConditionSubjectRef | null>[],
  value: ConditionSubjectRef,
  deletedLabel: string
): string[] => {
  if (isNil(value.subjectId)) {
    return [];
  }
  const categoryOption = options.find(
    (option) => option.value?.category === value.category && isNil(option.value?.subjectId)
  );
  if (isNil(categoryOption) || !isArray(categoryOption.children)) {
    return [];
  }
  return [...flatten([categoryOption.label]), deletedLabel];
};

export const getValLabel = (
  category: ConditionCategory,
  type: ConditionType | null,
  subjectId: string | null,
  label: string,
  group?: string
) => ({
  value: {
    category: category,
    subjectId: subjectId,
    type: type,
  },
  group,
  label: label,
});
export const getSubjectLabels = (
  category: ConditionCategory,
  subjects: ({ __typename?: "RateConditionSubject" } & RateConditionSubjectFragment)[] | undefined
) =>
  subjects?.map(({ type, subjectId, label: subjectLabel, group }) =>
    getValLabel(category, type, subjectId ?? null, subjectLabel, group ?? undefined)
  ) ?? [];
export const sortSubject = (
  rateConditionOptions: {
    value: {
      category: ConditionCategory;
      subjectId: string | null;
      type: ConditionType | null;
    };
    group: string | undefined;
    label: string;
  }[],
  locale: string
) =>
  sortLocalized(rateConditionOptions, ({ group, label: subjectLabel }) => [group, subjectLabel].join("/"), locale) ??
  [];
